import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertTriangle, Loader2, Calendar, MapPin, Users, CheckCircle, RefreshCw, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { courseCancellationApi } from '../../lib/api';

const eur = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

/**
 * Öffentliche Seite: Teilnehmer klickt auf den Umbuchungs-Link in der Absage-
 * Mail und wählt zwischen Umbuchung auf einen Ersatz-Termin oder Erstattung.
 */
export default function RebookChoicePage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [choice, setChoice] = useState(null); // "rebook" | "refund"
    const [selectedTarget, setSelectedTarget] = useState('');
    const [iban, setIban] = useState('');
    const [holder, setHolder] = useState('');
    const [bic, setBic] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(null); // {action, msg}

    useEffect(() => {
        if (!token) return;
        courseCancellationApi.getTokenInfo(token)
            .then((r) => {
                setInfo(r.data);
                if ((r.data.candidates || []).length > 0) setSelectedTarget(r.data.candidates[0].id);
            })
            .catch((e) => setError(e.response?.data?.detail || 'Link konnte nicht geladen werden'))
            .finally(() => setLoading(false));
    }, [token]);

    const submitRebook = async () => {
        if (!selectedTarget) { toast.error('Bitte einen Termin auswählen'); return; }
        setSubmitting(true);
        try {
            await courseCancellationApi.tokenRebook(token, selectedTarget);
            const target = info.candidates.find((c) => c.id === selectedTarget);
            setDone({ action: 'rebook', target });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Umbuchung fehlgeschlagen');
        } finally {
            setSubmitting(false);
        }
    };

    const submitRefund = async () => {
        const needsBank = (info.booking?.paid_amount || 0) > 0;
        const payload = {};
        if (needsBank) {
            if (!iban.trim() || !holder.trim()) {
                toast.error('Bitte IBAN und Kontoinhaber angeben');
                return;
            }
            payload.iban = iban.trim();
            payload.account_holder = holder.trim();
            if (bic.trim()) payload.bic = bic.trim();
        }
        setSubmitting(true);
        try {
            const res = await courseCancellationApi.tokenRefund(token, payload);
            setDone({ action: 'refund', paid_amount: res.data.paid_amount });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erstattung konnte nicht erfasst werden');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted/20">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted/20 p-4">
                <Card className="max-w-md w-full" data-testid="rebook-error-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-red-700">
                            <AlertTriangle className="h-5 w-5" /> Link nicht mehr gültig
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        <p>{error}</p>
                        <p className="text-muted-foreground">
                            Falls Sie unsicher sind, was mit Ihrer Buchung passiert ist,
                            loggen Sie sich in Ihrem Portal ein oder kontaktieren Sie die BGPR-Akademie direkt.
                        </p>
                        <Button onClick={() => navigate('/portal')} className="w-full">Zum Portal</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (done) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-muted/20 p-4">
                <Card className="max-w-md w-full" data-testid="rebook-done-card">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-emerald-700">
                            <CheckCircle className="h-6 w-6" /> Vielen Dank!
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm">
                        {done.action === 'rebook' ? (
                            <>
                                <p>Wir haben Sie erfolgreich auf den Ersatz-Termin umgebucht:</p>
                                <div className="border rounded-md p-3 bg-emerald-50">
                                    <div className="font-semibold">{done.target?.title}</div>
                                    <div className="text-xs text-muted-foreground">
                                        {done.target?.course_number} · {done.target?.date} · {done.target?.location}
                                    </div>
                                </div>
                                <p>Ihre bereits geleistete Zahlung wurde übernommen. Eine Bestätigung sehen Sie in Ihrem Portal.</p>
                            </>
                        ) : (
                            <>
                                <p>Ihre Erstattungs-Wahl wurde erfasst.</p>
                                {done.paid_amount > 0 && (
                                    <div className="border rounded-md p-3 bg-emerald-50">
                                        <div className="text-xs text-muted-foreground">Erstattungsbetrag</div>
                                        <div className="text-xl font-bold text-emerald-800">{eur(done.paid_amount)}</div>
                                    </div>
                                )}
                                <p>Die Rückerstattung wird zeitnah durch die Geschäftsstelle veranlasst.</p>
                            </>
                        )}
                        <Button onClick={() => navigate('/portal')} className="w-full" data-testid="rebook-done-portal-btn">
                            Zum Portal
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const s = info.seminar || {};
    const b = info.booking || {};
    const paid = b.paid_amount || 0;
    const candidates = info.candidates || [];

    return (
        <div className="min-h-screen bg-muted/20 py-8 px-4">
            <div className="max-w-2xl mx-auto space-y-6" data-testid="rebook-choice-page">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-red-800">
                            <AlertTriangle className="h-5 w-5" />
                            Kurs abgesagt: {s.title}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
                            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {s.date}</span>
                            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {s.location}</span>
                        </div>
                        {info.cancellation_reason && (
                            <div className="border rounded-md p-3 bg-amber-50 border-amber-200">
                                <div className="text-xs font-semibold text-amber-900 mb-0.5">Begründung</div>
                                <div className="text-amber-900">{info.cancellation_reason}</div>
                            </div>
                        )}
                        <p>
                            Sehr geehrte/r <strong>{b.participant_name}</strong>, wir bedauern die Umstände. Bitte wählen Sie,
                            wie wir mit Ihrer Buchung verfahren sollen{paid > 0 ? ` (bereits gezahlt: ${eur(paid)})` : ''}.
                        </p>
                    </CardContent>
                </Card>

                {/* Wahl-Karten */}
                <div className="grid gap-3 sm:grid-cols-2">
                    <button
                        type="button"
                        onClick={() => setChoice('rebook')}
                        disabled={candidates.length === 0}
                        className={`text-left border rounded-lg p-4 transition-all ${
                            choice === 'rebook' ? 'border-primary bg-primary/5 ring-2 ring-primary/20' :
                            candidates.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50'
                        }`}
                        data-testid="choice-rebook"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <RefreshCw className="h-4 w-4 text-primary" />
                            <span className="font-semibold">Umbuchung</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {candidates.length > 0
                                ? `${candidates.length} alternativer Termin${candidates.length === 1 ? '' : 'e'} verfügbar. Ihre Zahlung wandert mit.`
                                : 'Aktuell kein Ersatz-Termin verfügbar.'}
                        </p>
                    </button>
                    <button
                        type="button"
                        onClick={() => setChoice('refund')}
                        className={`text-left border rounded-lg p-4 transition-all ${
                            choice === 'refund' ? 'border-primary bg-primary/5 ring-2 ring-primary/20' : 'hover:border-primary/50'
                        }`}
                        data-testid="choice-refund"
                    >
                        <div className="flex items-center gap-2 mb-1">
                            <Undo2 className="h-4 w-4 text-primary" />
                            <span className="font-semibold">Erstattung</span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {paid > 0 ? `${eur(paid)} zurück auf Ihr Konto.` : 'Keine Zahlung erfolgt — nur Stornierung.'}
                        </p>
                    </button>
                </div>

                {/* Auswahl-Formular */}
                {choice === 'rebook' && candidates.length > 0 && (
                    <Card>
                        <CardHeader><CardTitle className="text-base">Ersatz-Termin wählen</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                            {candidates.map((c) => (
                                <label
                                    key={c.id}
                                    className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition-colors ${
                                        selectedTarget === c.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/30'
                                    }`}
                                    data-testid={`rebook-candidate-${c.id}`}
                                >
                                    <input
                                        type="radio"
                                        name="target"
                                        value={c.id}
                                        checked={selectedTarget === c.id}
                                        onChange={(e) => setSelectedTarget(e.target.value)}
                                        className="mt-1"
                                    />
                                    <div className="flex-1 text-sm">
                                        <div className="font-semibold">{c.title}</div>
                                        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                                            <span>{c.course_number}</span>
                                            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {c.date}</span>
                                            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {c.location}</span>
                                            <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {c.free_seats}/{c.max_participants} frei</span>
                                        </div>
                                    </div>
                                </label>
                            ))}
                            <Button
                                onClick={submitRebook}
                                disabled={submitting || !selectedTarget}
                                className="w-full"
                                data-testid="rebook-submit"
                            >
                                {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                                Umbuchung bestätigen
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {choice === 'refund' && (
                    <Card>
                        <CardHeader><CardTitle className="text-base">Erstattung erfassen</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                            {paid > 0 ? (
                                <>
                                    <div className="text-sm">
                                        Bitte geben Sie Ihre Bankverbindung an. Die Rückerstattung erfolgt zeitnah durch die Geschäftsstelle.
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="holder">Kontoinhaber *</Label>
                                        <Input id="holder" value={holder} onChange={(e) => setHolder(e.target.value)} data-testid="refund-holder" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="iban">IBAN *</Label>
                                        <Input id="iban" value={iban} onChange={(e) => setIban(e.target.value)} placeholder="DE00 0000 0000 0000 0000 00" data-testid="refund-iban" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="bic">BIC (optional)</Label>
                                        <Input id="bic" value={bic} onChange={(e) => setBic(e.target.value)} data-testid="refund-bic" />
                                    </div>
                                </>
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    Da noch keine Zahlung erfolgt ist, wird lediglich Ihre Buchung storniert. Klicken Sie auf „Bestätigen".
                                </p>
                            )}
                            <Button
                                onClick={submitRefund}
                                disabled={submitting}
                                className="w-full"
                                data-testid="refund-submit"
                            >
                                {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Undo2 className="h-4 w-4 mr-1.5" />}
                                Erstattung bestätigen
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}
